/**
 * Phase 5.2: DimensionIsolationService
 *
 * This service ensures that single-dimension commands (taller, wider, etc.)
 * ONLY modify their target dimension, preventing dimension leakage where
 * "taller" accidentally affects width or "wider" affects height.
 *
 * Key Features:
 * - Analyzes commands to determine which dimensions should be affected
 * - Filters AI backend responses to remove unintended dimension changes
 * - Validates that changes match command intent (increase vs decrease)
 * - Works with Phase 5.1 CommandStateManager for accurate current values
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */


import { logger } from '@/utils/logger';

/**
 * Result of analyzing a command for dimension intent
 */
export interface DimensionAnalysis {
  /** Whether this command affects width */
  affectsWidth: boolean;
  /** Whether this command affects height */
  affectsHeight: boolean;
  /** Whether width should be preserved (not changed) */
  preserveWidth: boolean;
  /** Whether height should be preserved (not changed) */
  preserveHeight: boolean;
  /** The intended direction of width change */
  widthModifier: 'increase' | 'decrease' | 'none';
  /** The intended direction of height change */
  heightModifier: 'increase' | 'decrease' | 'none';
  /** The original command analyzed */
  originalCommand: string;
  /** Whether this is a dimension-related command at all */
  isDimensionCommand: boolean;
}

/**
 * Property change object structure (matches existing PropertyChange type)
 */
export interface DimensionPropertyChange {
  style?: Record<string, unknown>;
  size?: { width?: string | number; height?: string | number };
  properties?: Record<string, unknown>;
  responsivePosition?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Result of validating dimension changes
 */
export interface DimensionValidation {
  /** Whether all changes are valid */
  valid: boolean;
  /** List of validation issues found */
  issues: string[];
  /** Suggested corrections */
  corrections?: {
    width?: number;
    height?: number;
  };
}

/**
 * Command patterns that affect ONLY height
 */
const HEIGHT_ONLY_PATTERNS: RegExp[] = [
  /\b(taller|increase height|more height|grow taller|stretch height)\b/i,
  /\b(shorter|decrease height|less height|shrink height|reduce height)\b/i,
  /\b(\d+)%\s*taller\b/i,
  /\b(\d+)%\s*shorter\b/i,
  /\btwice\s*(?:as\s*)?tall\b/i,
  /\bdouble\s*(?:the\s*)?height\b/i,
  /\bhalf\s*(?:as\s*)?tall\b/i,
  /\bhalf\s*(?:the\s*)?height\b/i,
];

/**
 * Command patterns that affect ONLY width
 */
const WIDTH_ONLY_PATTERNS: RegExp[] = [
  /\b(wider|increase width|more width|grow wider|stretch width)\b/i,
  /\b(narrower|decrease width|less width|shrink width|reduce width)\b/i,
  /\b(\d+)%\s*wider\b/i,
  /\b(\d+)%\s*narrower\b/i,
  /\btwice\s*(?:as\s*)?wide\b/i,
  /\bdouble\s*(?:the\s*)?width\b/i,
  /\bhalf\s*(?:as\s*)?wide\b/i,
  /\bhalf\s*(?:the\s*)?width\b/i,
];

/**
 * Command patterns that affect BOTH dimensions
 */
const BOTH_DIMENSIONS_PATTERNS: RegExp[] = [
  /\b(bigger|larger|grow|expand|scale up|enlarge)\b/i,
  /\b(smaller|tinier|shrink|scale down|reduce)\b/i,
  /\b(\d+)%\s*(larger|bigger)\b/i,
  /\b(\d+)%\s*(smaller|tinier)\b/i,
  /\bdouble\s*(?:the\s*)?size\b/i,
  /\btriple\s*(?:the\s*)?size\b/i,
  /\bhalf\s*(?:the\s*)?size\b/i,
  /\b(increase|decrease)\s*(?:by\s*)?\d+\s*(?:px)?\s*$/i,
];

/**
 * Height increase indicators
 */
const HEIGHT_INCREASE_PATTERNS: RegExp[] = [
  /\btaller\b/i,
  /\bincrease height\b/i,
  /\bmore height\b/i,
  /\bgrow taller\b/i,
  /\bstretch height\b/i,
  /\bdouble\s*(?:the\s*)?height\b/i,
  /\btwice\s*(?:as\s*)?tall\b/i,
  /\b(\d+)%\s*taller\b/i,
];

/**
 * Height decrease indicators
 */
const HEIGHT_DECREASE_PATTERNS: RegExp[] = [
  /\bshorter\b/i,
  /\bdecrease height\b/i,
  /\bless height\b/i,
  /\bshrink height\b/i,
  /\breduce height\b/i,
  /\bhalf\s*(?:the\s*)?height\b/i,
  /\bhalf\s*(?:as\s*)?tall\b/i,
  /\b(\d+)%\s*shorter\b/i,
];

/**
 * Width increase indicators
 */
const WIDTH_INCREASE_PATTERNS: RegExp[] = [
  /\bwider\b/i,
  /\bincrease width\b/i,
  /\bmore width\b/i,
  /\bgrow wider\b/i,
  /\bstretch width\b/i,
  /\bdouble\s*(?:the\s*)?width\b/i,
  /\btwice\s*(?:as\s*)?wide\b/i,
  /\b(\d+)%\s*wider\b/i,
];

/**
 * Width decrease indicators
 */
const WIDTH_DECREASE_PATTERNS: RegExp[] = [
  /\bnarrower\b/i,
  /\bdecrease width\b/i,
  /\bless width\b/i,
  /\bshrink width\b/i,
  /\breduce width\b/i,
  /\bhalf\s*(?:the\s*)?width\b/i,
  /\bhalf\s*(?:as\s*)?wide\b/i,
  /\b(\d+)%\s*narrower\b/i,
];

/**
 * Both dimensions increase indicators
 */
const BOTH_INCREASE_PATTERNS: RegExp[] = [
  /\bbigger\b/i,
  /\blarger\b/i,
  /\bgrow\b/i,
  /\bexpand\b/i,
  /\benlarge\b/i,
  /\bscale up\b/i,
  /\bdouble\s*(?:the\s*)?size\b/i,
  /\btriple\s*(?:the\s*)?size\b/i,
  /\bincrease\s*(?:by\s*)?\d+/i,
  /\b(\d+)%\s*(larger|bigger)\b/i,
];

/**
 * Both dimensions decrease indicators
 */
const BOTH_DECREASE_PATTERNS: RegExp[] = [
  /\bsmaller\b/i,
  /\btinier\b/i,
  /\bshrink\b/i,
  /\bscale down\b/i,
  /\breduce\b/i,
  /\bhalf\s*(?:the\s*)?size\b/i,
  /\bhalve\b/i,
  /\bdecrease\s*(?:by\s*)?\d+/i,
  /\b(\d+)%\s*(smaller|tinier)\b/i,
];

/**
 * DimensionIsolationService - Singleton service for dimension analysis and filtering
 */
class DimensionIsolationService {
  private static instance: DimensionIsolationService | null = null;

  private constructor() {
    logger.debug("DimensionIsolationService initialized");
  }

  /**
   * Get the singleton instance of DimensionIsolationService
   */
  static getInstance(): DimensionIsolationService {
    if (!this.instance) {
      this.instance = new DimensionIsolationService();
    }
    return this.instance;
  }

  /**
   * Analyzes a command to determine which dimensions it should affect.
   * This is the core method for dimension isolation.
   *
   * @param command The user's command string
   * @returns DimensionAnalysis object with full analysis
   */
  analyzeCommand(command: string): DimensionAnalysis {
    const normalized = command.toLowerCase().trim();

    // Check if this is a dimension-related command at all
    const isDimensionCommand = this.isDimensionCommand(normalized);

    if (!isDimensionCommand) {
      console.log('[Phase 5.2] Not a dimension command:', command);
      return {
        affectsWidth: false,
        affectsHeight: false,
        preserveWidth: false,
        preserveHeight: false,
        widthModifier: 'none',
        heightModifier: 'none',
        originalCommand: command,
        isDimensionCommand: false,
      };
    }

    // Check for height-only commands
    const isHeightOnly = HEIGHT_ONLY_PATTERNS.some(p => p.test(normalized));
    const isWidthOnly = WIDTH_ONLY_PATTERNS.some(p => p.test(normalized));
    const isBothDimensions = BOTH_DIMENSIONS_PATTERNS.some(p => p.test(normalized));

    // Determine if increase or decrease for each dimension
    const heightIncrease = HEIGHT_INCREASE_PATTERNS.some(p => p.test(normalized)) ||
                          (isBothDimensions && BOTH_INCREASE_PATTERNS.some(p => p.test(normalized)));
    const heightDecrease = HEIGHT_DECREASE_PATTERNS.some(p => p.test(normalized)) ||
                          (isBothDimensions && BOTH_DECREASE_PATTERNS.some(p => p.test(normalized)));
    const widthIncrease = WIDTH_INCREASE_PATTERNS.some(p => p.test(normalized)) ||
                         (isBothDimensions && BOTH_INCREASE_PATTERNS.some(p => p.test(normalized)));
    const widthDecrease = WIDTH_DECREASE_PATTERNS.some(p => p.test(normalized)) ||
                         (isBothDimensions && BOTH_DECREASE_PATTERNS.some(p => p.test(normalized)));

    // Build the analysis result
    const affectsHeight = isHeightOnly || isBothDimensions;
    const affectsWidth = isWidthOnly || isBothDimensions;

    // Determine preservation - single-dimension commands should preserve the other
    const preserveWidth = isHeightOnly && !isBothDimensions && !isWidthOnly;
    const preserveHeight = isWidthOnly && !isBothDimensions && !isHeightOnly;

    // Determine modifiers
    let widthModifier: 'increase' | 'decrease' | 'none' = 'none';
    let heightModifier: 'increase' | 'decrease' | 'none' = 'none';

    if (affectsWidth) {
      widthModifier = widthIncrease ? 'increase' : (widthDecrease ? 'decrease' : 'none');
    }
    if (affectsHeight) {
      heightModifier = heightIncrease ? 'increase' : (heightDecrease ? 'decrease' : 'none');
    }

    const analysis: DimensionAnalysis = {
      affectsWidth,
      affectsHeight,
      preserveWidth,
      preserveHeight,
      widthModifier,
      heightModifier,
      originalCommand: command,
      isDimensionCommand: true,
    };

    console.log('[Phase 5.2] Dimension analysis:', {
      command: normalized,
      analysis: {
        affectsWidth,
        affectsHeight,
        preserveWidth,
        preserveHeight,
        widthModifier,
        heightModifier,
      },
    });

    return analysis;
  }

  /**
   * Check if a command is dimension-related at all
   */
  private isDimensionCommand(command: string): boolean {
    const allPatterns = [
      ...HEIGHT_ONLY_PATTERNS,
      ...WIDTH_ONLY_PATTERNS,
      ...BOTH_DIMENSIONS_PATTERNS,
    ];

    return allPatterns.some(p => p.test(command));
  }

  /**
   * Filters property changes to only include dimensions that should be affected.
   * Removes dimensions that should be preserved according to the analysis.
   *
   * @param changes The property changes from the AI backend
   * @param analysis The dimension analysis result
   * @param currentState The current dimensions (for fallback values)
   * @returns Filtered property changes
   */
  filterPropertyChanges(
    changes: DimensionPropertyChange,
    analysis: DimensionAnalysis,
    currentState: { width: number | string; height: number | string }
  ): DimensionPropertyChange {
    // If not a dimension command, return unchanged
    if (!analysis.isDimensionCommand) {
      return changes;
    }

    const filtered = structuredClone(changes);

    // Filter size changes
    if (filtered.size) {
      if (analysis.preserveWidth && filtered.size.width !== undefined) {
        console.log('[Phase 5.2] Removing width from size - should be preserved:', filtered.size.width);
        delete filtered.size.width;
      }

      if (analysis.preserveHeight && filtered.size.height !== undefined) {
        console.log('[Phase 5.2] Removing height from size - should be preserved:', filtered.size.height);
        delete filtered.size.height;
      }

      // Clean up empty size object
      if (filtered.size && Object.keys(filtered.size).length === 0) {
        delete filtered.size;
      }
    }

    // Filter style dimension changes as well
    if (filtered.style) {
      if (analysis.preserveWidth && filtered.style.width !== undefined) {
        console.log('[Phase 5.2] Removing width from style - should be preserved:', filtered.style.width);
        delete filtered.style.width;
      }

      if (analysis.preserveHeight && filtered.style.height !== undefined) {
        console.log('[Phase 5.2] Removing height from style - should be preserved:', filtered.style.height);
        delete filtered.style.height;
      }

      // Clean up empty style object
      if (filtered.style && Object.keys(filtered.style).length === 0) {
        delete filtered.style;
      }
    }

    // Handle responsive position style changes if present
    if (filtered.responsivePosition) {
      const rp = filtered.responsivePosition as Record<string, unknown>;
      const breakpoints = rp.breakpoints as Record<string, { style?: Record<string, unknown> }> | undefined;

      if (breakpoints) {
        for (const bp of Object.keys(breakpoints)) {
          const bpStyle = breakpoints[bp]?.style;
          if (bpStyle) {
            if (analysis.preserveWidth && bpStyle.width !== undefined) {
              console.log(`[Phase 5.2] Removing width from ${bp} breakpoint style - should be preserved`);
              delete bpStyle.width;
            }
            if (analysis.preserveHeight && bpStyle.height !== undefined) {
              console.log(`[Phase 5.2] Removing height from ${bp} breakpoint style - should be preserved`);
              delete bpStyle.height;
            }
          }
        }
      }
    }

    console.log('[Phase 5.2] Filtered changes:', {
      original: changes,
      filtered,
      preserveWidth: analysis.preserveWidth,
      preserveHeight: analysis.preserveHeight,
    });

    return filtered;
  }

  /**
   * Validates that changes are consistent with the command's intent.
   * Checks that increase commands actually increase and decrease commands decrease.
   *
   * @param changes The property changes to validate
   * @param analysis The dimension analysis result
   * @param currentState The current dimensions in pixels
   * @returns Validation result with any issues found
   */
  validateChanges(
    changes: DimensionPropertyChange,
    analysis: DimensionAnalysis,
    currentState: { width: number; height: number }
  ): DimensionValidation {
    const issues: string[] = [];
    const corrections: { width?: number; height?: number } = {};

    // If not a dimension command, no validation needed
    if (!analysis.isDimensionCommand) {
      return { valid: true, issues: [] };
    }

    // Check width changes
    const rawWidth = changes.size?.width ?? changes.style?.width;
    const newWidth = this.extractNumericValue(
      typeof rawWidth === 'string' || typeof rawWidth === 'number' ? rawWidth : undefined
    );
    if (newWidth !== null && analysis.affectsWidth) {
      if (analysis.widthModifier === 'increase' && newWidth <= currentState.width) {
        issues.push(
          `Width should increase but got ${newWidth}px (current: ${currentState.width}px)`
        );
        // Suggest a correction: 50% increase
        corrections.width = Math.round(currentState.width * 1.5);
      }
      if (analysis.widthModifier === 'decrease' && newWidth >= currentState.width) {
        issues.push(
          `Width should decrease but got ${newWidth}px (current: ${currentState.width}px)`
        );
        // Suggest a correction: 25% decrease
        corrections.width = Math.round(currentState.width * 0.75);
      }
    }

    // Check if width was changed when it should be preserved
    if (analysis.preserveWidth && newWidth !== null) {
      issues.push('Width changed but should be preserved');
    }

    // Check height changes
    const rawHeight = changes.size?.height ?? changes.style?.height;
    const newHeight = this.extractNumericValue(
      typeof rawHeight === 'string' || typeof rawHeight === 'number' ? rawHeight : undefined
    );
    if (newHeight !== null && analysis.affectsHeight) {
      if (analysis.heightModifier === 'increase' && newHeight <= currentState.height) {
        issues.push(
          `Height should increase but got ${newHeight}px (current: ${currentState.height}px)`
        );
        // Suggest a correction: 50% increase
        corrections.height = Math.round(currentState.height * 1.5);
      }
      if (analysis.heightModifier === 'decrease' && newHeight >= currentState.height) {
        issues.push(
          `Height should decrease but got ${newHeight}px (current: ${currentState.height}px)`
        );
        // Suggest a correction: 25% decrease
        corrections.height = Math.round(currentState.height * 0.75);
      }
    }

    // Check if height was changed when it should be preserved
    if (analysis.preserveHeight && newHeight !== null) {
      issues.push('Height changed but should be preserved');
    }

    const result: DimensionValidation = {
      valid: issues.length === 0,
      issues,
    };

    if (Object.keys(corrections).length > 0) {
      result.corrections = corrections;
    }

    if (!result.valid) {
      console.log('[Phase 5.2] Validation issues:', {
        command: analysis.originalCommand,
        issues,
        corrections,
      });
    }

    return result;
  }

  /**
   * Applies corrections to invalid dimension changes.
   * Uses the corrections suggested by validateChanges.
   *
   * @param changes The property changes to correct
   * @param analysis The dimension analysis result
   * @param validation The validation result with corrections
   * @returns Corrected property changes
   */
  applyCorrections(
    changes: DimensionPropertyChange,
    analysis: DimensionAnalysis,
    validation: DimensionValidation
  ): DimensionPropertyChange {
    if (validation.valid || !validation.corrections) {
      return changes;
    }

    const corrected = structuredClone(changes);

    // Ensure size object exists
    if (!corrected.size) {
      corrected.size = {};
    }

    // Apply width correction
    if (validation.corrections.width !== undefined && analysis.affectsWidth) {
      console.log(
        `[Phase 5.2] Applying width correction: ${corrected.size.width} -> ${validation.corrections.width}px`
      );
      corrected.size.width = `${validation.corrections.width}px`;
    }

    // Apply height correction
    if (validation.corrections.height !== undefined && analysis.affectsHeight) {
      console.log(
        `[Phase 5.2] Applying height correction: ${corrected.size.height} -> ${validation.corrections.height}px`
      );
      corrected.size.height = `${validation.corrections.height}px`;
    }

    return corrected;
  }

  /**
   * Full processing pipeline: analyze, filter, validate, and correct
   *
   * @param command The user's command
   * @param changes The property changes from AI backend
   * @param currentState The current dimensions in pixels
   * @returns Processed property changes
   */
  processChanges(
    command: string,
    changes: DimensionPropertyChange,
    currentState: { width: number; height: number }
  ): {
    changes: DimensionPropertyChange;
    analysis: DimensionAnalysis;
    validation: DimensionValidation;
    wasFiltered: boolean;
    wasCorrected: boolean;
  } {
    // Step 1: Analyze the command
    const analysis = this.analyzeCommand(command);

    // Step 2: Filter to only intended dimensions
    const filtered = this.filterPropertyChanges(changes, analysis, currentState);
    const wasFiltered = JSON.stringify(changes) !== JSON.stringify(filtered);

    // Step 3: Validate the changes
    const validation = this.validateChanges(filtered, analysis, currentState);

    // Step 4: Apply corrections if needed
    let finalChanges = filtered;
    let wasCorrected = false;

    if (!validation.valid && validation.corrections) {
      finalChanges = this.applyCorrections(filtered, analysis, validation);
      wasCorrected = true;
    }

    console.log('[Phase 5.2] Process changes complete:', {
      command,
      wasFiltered,
      wasCorrected,
      validationIssues: validation.issues,
    });

    return {
      changes: finalChanges,
      analysis,
      validation,
      wasFiltered,
      wasCorrected,
    };
  }

  /**
   * Extract numeric value from a dimension string or number
   */
  private extractNumericValue(value: string | number | undefined): number | null {
    if (value === undefined || value === null) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    const strValue = String(value).trim().toLowerCase();

    // Handle 'auto' - not a numeric value
    if (strValue === 'auto') {
      return null;
    }

    // Extract numeric portion
    const match = strValue.match(/(-?\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Check if a command is a height-only command
   */
  isHeightOnlyCommand(command: string): boolean {
    const normalized = command.toLowerCase().trim();
    const isHeightCommand = HEIGHT_ONLY_PATTERNS.some(p => p.test(normalized));
    const isWidthCommand = WIDTH_ONLY_PATTERNS.some(p => p.test(normalized));
    const isBothCommand = BOTH_DIMENSIONS_PATTERNS.some(p => p.test(normalized));

    return isHeightCommand && !isWidthCommand && !isBothCommand;
  }

  /**
   * Check if a command is a width-only command
   */
  isWidthOnlyCommand(command: string): boolean {
    const normalized = command.toLowerCase().trim();
    const isHeightCommand = HEIGHT_ONLY_PATTERNS.some(p => p.test(normalized));
    const isWidthCommand = WIDTH_ONLY_PATTERNS.some(p => p.test(normalized));
    const isBothCommand = BOTH_DIMENSIONS_PATTERNS.some(p => p.test(normalized));

    return isWidthCommand && !isHeightCommand && !isBothCommand;
  }

  /**
   * Check if a command affects both dimensions
   */
  isBothDimensionsCommand(command: string): boolean {
    const normalized = command.toLowerCase().trim();
    return BOTH_DIMENSIONS_PATTERNS.some(p => p.test(normalized));
  }
}

// Export singleton instance
export const dimensionIsolationService = DimensionIsolationService.getInstance();

// Also export the class for testing
export { DimensionIsolationService };
